import { hoursToMilliseconds, minutesToMilliseconds } from '@gonfalon/datetime/dateFns';
import { daysToMilliseconds } from '@gonfalon/datetime/daysToMilliseconds';
import { msg } from '@gonfalon/messages/msg';
import { MAX_WINDOW_DAYS } from '@gonfalon/metrics/constants';

import type { WindowUnit } from '../../CreateMetricModal/formUtils';

export const WINDOW_UNITS: Array<{ id: WindowUnit; maxInUnit: number }> = [
  { id: 'days', maxInUnit: MAX_WINDOW_DAYS },
  { id: 'hours', maxInUnit: MAX_WINDOW_DAYS * 24 },
  { id: 'minutes', maxInUnit: MAX_WINDOW_DAYS * 24 * 60 },
];

export const windowUnitLabel = (unit: WindowUnit) => msg('metrics.measurementWindow.unitName', { unit });

export function getStartError(val: number | undefined, maxInUnit?: number, unitLabel?: string): string | undefined {
  if (val === undefined || Number.isNaN(val)) {
    return msg('metrics.measurementWindow.startRequired');
  }
  if (val < 0) {
    return msg('metrics.measurementWindow.startNegative');
  }
  if (!Number.isInteger(val)) {
    return msg('metrics.measurementWindow.startNotInteger');
  }
  if (maxInUnit !== undefined && val > maxInUnit) {
    return msg('metrics.measurementWindow.startTooLarge', {
      maxDays: MAX_WINDOW_DAYS,
      max: maxInUnit,
      unit: unitLabel ?? '',
    });
  }
  return undefined;
}

export function getEndError(
  val: number | undefined,
  maxInUnit: number,
  unitLabel: string,
  start: number | undefined,
): string | undefined {
  if (val === undefined || Number.isNaN(val)) {
    return msg('metrics.measurementWindow.endRequired');
  }
  if (!Number.isInteger(val)) {
    return msg('metrics.measurementWindow.endNotInteger');
  }
  if (val > maxInUnit) {
    return msg('metrics.measurementWindow.endTooLarge', { maxDays: MAX_WINDOW_DAYS, max: maxInUnit, unit: unitLabel });
  }
  if (typeof start === 'number' && !Number.isNaN(start) && val <= start) {
    return msg('metrics.measurementWindow.endBeforeStart', { end: val, start });
  }
  return undefined;
}

/** Convert ratio-term form values to API millisecond offsets, gated on windowMode. */
export function toRatioWindowOffsets(
  windowMode: 'none' | 'custom' | undefined,
  windowStart: number | undefined,
  windowEnd: number | undefined,
  windowUnit: WindowUnit = 'days',
): { windowStartOffset?: number; windowEndOffset?: number } {
  if (windowMode !== 'custom') {
    return { windowStartOffset: undefined, windowEndOffset: undefined };
  }
  return toWindowOffsets(windowStart, windowEnd, windowUnit);
}

/** Convert form values to API millisecond offsets. Returns undefined for both when no window is set. */
export function toWindowOffsets(
  windowStart: number | undefined,
  windowEnd: number | undefined,
  windowUnit: WindowUnit = 'days',
): { windowStartOffset?: number; windowEndOffset?: number } {
  if (windowStart === undefined || windowEnd === undefined) {
    return { windowStartOffset: undefined, windowEndOffset: undefined };
  }
  const toMs =
    windowUnit === 'hours'
      ? hoursToMilliseconds
      : windowUnit === 'minutes'
        ? minutesToMilliseconds
        : daysToMilliseconds;
  return {
    windowStartOffset: toMs(windowStart),
    windowEndOffset: toMs(windowEnd),
  };
}
